import React, { useEffect, useState } from 'react';
import { useAccount, useReadContract } from 'wagmi';
import { formatUnits } from 'viem';
import { vaultConfig } from '../config/vaultConfig-v2';
import { useVaultAPYv2 } from '../hooks/useVaultAPYv2';
import './UserEarningsV2.css';

function UserEarningsV2() {
  const { address, isConnected } = useAccount();
  const [userStats, setUserStats] = useState(null);
  const { apy: vaultApy } = useVaultAPYv2();

  // Get user stats from VaultCore contract
  const { data: rawUserStats, isLoading, error } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'getUserStats',
    args: address ? [address] : undefined,
    enabled: isConnected && !!address,
    watch: true,
  });

  // Get user's vUSDC balance for simpler display
  const { data: userShares } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    watch: true,
    enabled: !!address,
  });

  useEffect(() => {
    if (rawUserStats && !isLoading) {
      // Parse the user stats tuple (10 values)
      const [
        totalDeposited,
        totalWithdrawn,
        currentValue,
        unrealizedPnL,
        realizedPnL,
        totalPnL,
        roiPercentage,
        sharesOwned,
        firstDepositTime,
        depositCount
      ] = rawUserStats;

      setUserStats({
        totalDeposited,
        totalWithdrawn,
        currentValue,
        unrealizedPnL,
        realizedPnL,
        totalPnL,
        roiPercentage,
        sharesOwned,
        firstDepositTime,
        depositCount
      });
    }
  }, [rawUserStats, isLoading]);

  if (!isConnected) {
    return null;
  }

  // Check if user has a position
  const userSharesFormatted = userShares ? parseFloat(formatUnits(userShares, 6)) : 0;
  
  if (userSharesFormatted === 0) {
    return null; // Don't show anything if no position
  }

  if (error) {
    console.error('Error fetching user stats:', error);
    // Fallback to simple display if getUserStats fails
    return (
      <div className="user-position-summary">
        <h3>Your Position</h3>
        <div className="position-stats">
          <div className="position-stat">
            <span className="stat-label">vUSDC Balance</span>
            <span className="stat-value">{userSharesFormatted.toFixed(4)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !userStats) {
    return (
      <div className="user-position-summary">
        <h3>Your Position</h3>
        <div className="loading">Loading your stats...</div>
      </div>
    );
  }

  // Format values
  const formatUSDC = (value) => {
    if (!value) return '$0.00';
    const formatted = parseFloat(formatUnits(value, 6));
    return `$${formatted.toFixed(2)}`;
  };

  const formatPnL = (value) => {
    if (!value) return '$0.00';
    const formatted = parseFloat(formatUnits(value, 6));
    const sign = formatted >= 0 ? '+' : '';
    return `${sign}$${Math.abs(formatted).toFixed(2)}`;
  };

  const formatROI = (value) => {
    if (!value) return '0.00%';
    // ROI is in basis points (1/100 of a percent)
    const percentage = Number(value) / 100;
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(2)}%`;
  };

  // Calculate values
  const firstDepositDate = userStats.firstDepositTime > 0n 
    ? new Date(Number(userStats.firstDepositTime) * 1000).toLocaleDateString()
    : 'N/A';

  // Determine P&L status
  const isProfit = Number(userStats.totalPnL) >= 0;
  const unrealizedProfit = Number(userStats.unrealizedPnL) >= 0;
  const realizedProfit = Number(userStats.realizedPnL) >= 0;

  // Use vault APY for earnings calculations
  const currentAPY = vaultApy?.current || 8.5;
  const currentValueFormatted = parseFloat(formatUnits(userStats.currentValue, 6));
  const dailyEarnings = (currentValueFormatted * currentAPY) / 36500;
  const monthlyEarnings = (currentValueFormatted * currentAPY) / 1200;

  return (
    <div className="user-position-summary">
      <h3>Your Position & Performance</h3>
      
      {/* Main Position Stats */}
      <div className="position-stats">
        <div className="position-stat">
          <span className="stat-label">Portfolio Value</span>
          <span className="stat-value">{formatUSDC(userStats.currentValue)}</span>
        </div>
        <div className="position-stat">
          <span className="stat-label">vUSDC Balance</span>
          <span className="stat-value">{parseFloat(formatUnits(userStats.sharesOwned, 6)).toFixed(4)}</span>
        </div>
        <div className="position-stat">
          <span className="stat-label">Daily Earnings</span>
          <span className="stat-value">${dailyEarnings.toFixed(2)}</span>
        </div>
        <div className="position-stat">
          <span className="stat-label">Monthly Earnings</span>
          <span className="stat-value">${monthlyEarnings.toFixed(2)}</span>
        </div>
      </div>

      {/* P&L Stats */}
      <div className="position-stats" style={{ marginTop: '20px' }}>
        <div className="position-stat">
          <span className="stat-label">Total P&L</span>
          <span className={`stat-value ${isProfit ? 'profit' : 'loss'}`}>
            {formatPnL(userStats.totalPnL)}
          </span>
        </div>
        <div className="position-stat">
          <span className="stat-label">Unrealized P&L</span>
          <span className={`stat-value ${unrealizedProfit ? 'profit' : 'loss'}`}>
            {formatPnL(userStats.unrealizedPnL)}
          </span>
        </div>
        <div className="position-stat">
          <span className="stat-label">Realized P&L</span>
          <span className={`stat-value ${realizedProfit ? 'profit' : 'loss'}`}>
            {formatPnL(userStats.realizedPnL)}
          </span>
        </div>
        <div className="position-stat">
          <span className="stat-label">ROI</span>
          <span className={`stat-value ${isProfit ? 'profit' : 'loss'}`}>
            {formatROI(userStats.roiPercentage)}
          </span>
        </div>
      </div>

      {/* Activity Stats */}
      <div className="position-stats" style={{ marginTop: '20px' }}>
        <div className="position-stat">
          <span className="stat-label">Total Deposited</span>
          <span className="stat-value">{formatUSDC(userStats.totalDeposited)}</span>
        </div>
        <div className="position-stat">
          <span className="stat-label">Total Withdrawn</span>
          <span className="stat-value">{formatUSDC(userStats.totalWithdrawn)}</span>
        </div>
        <div className="position-stat">
          <span className="stat-label">First Deposit</span>
          <span className="stat-value">{firstDepositDate}</span>
        </div>
        <div className="position-stat">
          <span className="stat-label">Transactions</span>
          <span className="stat-value">{Number(userStats.depositCount)}</span>
        </div>
      </div>
    </div>
  );
}

export default UserEarningsV2;